import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WARN_WHEN_COLUMNS_REMAINING_BELOW } from '../../../shared/constants';
import { DrawerHeader } from '../drawer/DrawerHeader';
import { DrawerSearch } from '../drawer/DrawerSearch';
import { SidePanelDrawer } from '../drawer/SidePanelDrawer';
import type { ColumnConfiguratorDrawerProps } from './ColumnConfiguratorDrawer.types';
import { ColumnCounter } from './components/ColumnCounter';
import { ColumnList } from './components/ColumnList';
import { DrawerFooter } from './components/DrawerFooter';

const COLUMN_DRAWER_TITLE_ID = 'column-configurator-title';

export function ColumnConfiguratorDrawer({
  open,
  onCancel,
  onApply,
  onSaveView,
  allColumns,
  columnGroups,
  visibleKeys,
  onToggle,
  onResetToDefaults,
  selectedCount,
  effectiveMax,
  isAtMax,
}: ColumnConfiguratorDrawerProps) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');

  const progress = Math.min((selectedCount / effectiveMax) * 100, 100);
  const slotsRemaining = effectiveMax - selectedCount;
  const isLowSlots = slotsRemaining <= WARN_WHEN_COLUMNS_REMAINING_BELOW && slotsRemaining > 0;

  const groupedColumns = useMemo(() => {
    const searchLower = search.toLowerCase();
    return columnGroups
      .map(group => ({
        config: group,
        columns: allColumns
          .filter(c => c.configurable !== false && c.group === group.key)
          .filter(c => t(c.labelKey).toLowerCase().includes(searchLower)),
      }))
      .filter(g => g.columns.length > 0);
  }, [columnGroups, allColumns, search, t]);

  return (
    <SidePanelDrawer open={open} onClose={onCancel} ariaLabelledBy={COLUMN_DRAWER_TITLE_ID}>
      <DrawerHeader onCancel={onCancel} titleId={COLUMN_DRAWER_TITLE_ID} />

      <ColumnCounter
        selectedCount={selectedCount}
        effectiveMax={effectiveMax}
        isAtMax={isAtMax}
        isLowSlots={isLowSlots}
        slotsRemaining={slotsRemaining}
        progress={progress}
      />

      <DrawerSearch search={search} onChange={setSearch} onClear={() => setSearch('')} />

      <ColumnList
        groupedColumns={groupedColumns}
        visibleKeys={visibleKeys}
        isAtMax={isAtMax}
        onToggle={onToggle}
        hasNoResults={groupedColumns.length === 0}
      />

      <DrawerFooter
        onCancel={onCancel}
        onResetToDefaults={onResetToDefaults}
        onSaveView={onSaveView}
        onApply={onApply}
      />
    </SidePanelDrawer>
  );
}
